import * as React from 'react';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import { Label } from '../label/Label';
import { IBaseTextProps } from '../typography/Text';
import { useInverseBg } from '../theme/useInverseBg';
import { isEmpty } from 'lodash-es';

const EMPTY_VALUE = '-';

type LabelValueListProps = React.PropsWithChildren<{
  /**
   * Inverzní verze - typicky používáme na tmavším pozadí.
   */
  inverse?: boolean;
  className?: string;
  /**
   * Co se má zobrazit při prázdné hodnotě? Ve vhochozím stavu se použije '-'. Děděné na items - použije se pokud není u items definováno.
   */
  emptyValue?: React.ReactNode;
  /**
   * Zarovna hodnoty doprava? Děděné na items - použije se pokud není u items definováno.
   */
  alignRight?: boolean;
  /**
   * Počet sloupců pro layout, hodnota 2 je výchozí a je doporučená pro většinu použití.
   */
  cols?: number;
  /**
   * Má se list roztáhnout na šířku? - používat POUZE toto, nikoli např. w-100 apod.
   */
  full?: boolean;
  /**
   * Intent labelu, pokud není uvedeno, je default.
   */
  intent?: 'inactive' | 'default';
}>;

export interface ILabelValueListItemProps extends IBaseTextProps {
  className?: string;
  /**
   * Pokud title není uvedeno, zobrazuje se po najetí na hodnotu text z label.
   * Pokud title je uvedeno, zobrazuje se po najetí jak na label, tak na hodnotu.
   */
  title?: string;
  label: string;
  /**
   * Zarovna hodnoty doprava?
   */
  alignRight?: boolean;
  /**
   * Kolik sloupců má zabírat hodnota, výchozí hodnota je 1, jen pro variantu v LabelValueList.
   */
  span?: number;
  labelClassName?: string;
  contentClassName?: string;
  /**
   * Co se má zobrazit při prázdné hodnotě? Ve vhochozím stavu se použije '-'.
   */
  emptyValue?: React.ReactNode;
  /**
   * NENÍ použité, jen definováno jen kvůli sdílení komponenty pro DetailValueList.
   */
  horizontal?: boolean;
  /**
   * NENÍ použité, jen definováno jen kvůli sdílení komponenty pro DetailValueList.
   */
  lastItem?: boolean;
  /**
   * Vycentruje horionztálně label, nefunguje pokud je horizontal true.
   */
  centeredLabel?: boolean;
}

export type LabelValueListItemProps =
  React.PropsWithChildren<ILabelValueListItemProps>;

export const LabelValueList: React.FC<LabelValueListProps> = ({
  children,
  className,
  inverse,
  emptyValue = EMPTY_VALUE,
  alignRight,
  cols,
  full,
  intent,
}) => {
  const parentInverse = useInverseBg(inverse);

  const rows = [];
  let dataCols: any[] = [];
  let dataColsSpans = 0;
  React.Children.forEach(children, (child: any) => {
    if (child) {
      let realValue;
      if (
        child.props.children === '' ||
        child.props.children === null ||
        child.props.children === undefined
      ) {
        realValue = child.props.emptyValue || emptyValue;
      } else {
        realValue = child.props.children;
      }

      const valueSpan = child.props.span || 1;
      const labelValueSpan = 1 + valueSpan; // label + value
      if (dataColsSpans + labelValueSpan > (cols || 2)) {
        if (dataColsSpans === 0) {
          throw new Error(
            'Chybná definice sloupců - šířka label + hodnota je pro jednu položku věší než počet sloupců'
          );
        }
        rows.push(<tr key={rows.length}>{dataCols}</tr>);
        dataCols = [];
        dataColsSpans = 0;
      }

      const childParentInverse =
        child.props.inverse == undefined ? parentInverse : child.props.inverse;

      let valueCls = classNames(child.props.contentClassName, {
        [Classes.TYPOGRAPHY_DEFAULT]:
          child.props.intent === undefined && intent === 'default',
        [Classes.TYPOGRAPHY_INHERIT]:
          child.props.intent === undefined && intent === undefined,
        [Classes.TYPOGRAPHY_INVERSE]: childParentInverse,
        [Classes.TYPOGRAPHY_PRIMARY]: child.props.intent === 'primary',
        [Classes.TYPOGRAPHY_SECONDARY]: child.props.intent === 'secondary',
        [Classes.TYPOGRAPHY_DANGER]: child.props.intent === 'danger',
        [Classes.TYPOGRAPHY_SUCCESS]: child.props.intent === 'success',
        [Classes.TYPOGRAPHY_WARNING]: child.props.intent === 'warning',
        [Classes.TYPOGRAPHY_INACTIVE]:
          child.props.intent === undefined
            ? intent === 'inactive'
            : child.props.intent === 'inactive',
        [Classes.TYPOGRAPHY_STRONG]: child.props.strong,
        [Classes.TYPOGRAPHY_ITALIC]: child.props.italic,
        [Classes.TYPOGRAPHY_UNDERLINE]: child.props.underline,
      });
      let labelIntent: 'inactive' | 'default';
      switch (child.props.intent) {
        case 'inactive':
          labelIntent = 'inactive';
          break;
        default:
          labelIntent = 'default';
      }
      dataCols.push(
        <Cols
          labelIntent={child.props.intent ? labelIntent : intent}
          key={dataCols.length}
          title={child.props.title}
          inverse={parentInverse}
          label={child.props.label}
          className={child.props.className}
          value={realValue}
          span={valueSpan}
          alignRight={
            child.props.alignRight === undefined
              ? alignRight
              : child.props.alignRight
          }
          labelClassName={child.props.labelClassName}
          contentClassName={valueCls}
        />
      );
      dataColsSpans += labelValueSpan;
    }
  });
  if (dataCols.length > 0) {
    rows.push(<tr key={rows.length}>{dataCols}</tr>);
  }

  if (isEmpty(rows)) {
    return null;
  } else {
    return (
      <table
        className={classNames(className, Classes.LABEL_VALUE_LIST, {
          [Classes.LABEL_VALUE_LIST_FULL]: full,
        })}
      >
        {rows}
      </table>
    );
  }
};

const Cols: React.FC<{
  title?: string;
  inverse?: boolean;
  label: string;
  value: React.ReactNode;
  className?: string;
  span?: number;
  alignRight?: boolean;
  labelClassName?: string;
  contentClassName?: string;
  labelIntent?: 'inactive' | 'default';
}> = ({
  className,
  labelClassName,
  inverse,
  contentClassName,
  label,
  title,
  value,
  alignRight,
  span = 1,
  labelIntent,
}) => {
  return (
    <>
      <th
        className={classNames(
          labelClassName,
          className,
          Classes.LABEL_VALUE_LIST_ITEM_LABEL
        )}
      >
        <Label inverse={inverse} intent={labelIntent}>
          <span title={title}>{label}</span>
        </Label>
      </th>
      <td
        className={classNames(
          contentClassName,
          className,
          Classes.LABEL_VALUE_LIST_ITEM_VALUE,
          {
            [Classes.LABEL_VALUE_LIST_ITEM_VALUE_RIGHT]: alignRight,
          }
        )}
        colSpan={span}
      >
        <div title={title || label}>{value}</div>
      </td>
    </>
  );
};
